import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { MapPin, Home, Clock, ChevronRight, X } from 'lucide-react';
import { projects } from '../data/mock';

export const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const openGallery = (project, imageIndex = 0) => {
    setSelectedProject(project);
    setSelectedImageIndex(imageIndex);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
    setTimeout(() => {
      setSelectedProject(null);
      setSelectedImageIndex(0);
    }, 300);
  };

  const nextImage = () => {
    if (selectedProject) {
      setSelectedImageIndex((prev) => 
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <section id="projects" className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-4">
              Featured Projects
            </h2>
            <p className="text-lg text-charcoal-400 max-w-2xl mx-auto">
              Discover our flagship residential developments, crafted with excellence and designed for modern living
            </p>
          </div>

          {/* Projects Grid */}
          <div className="space-y-20">
            {projects.map((project, index) => (
              <div
                key={project.id}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } gap-10 items-center`}
              >
                {/* Image Gallery Preview */}
                <div className="w-full lg:w-1/2">
                  <div className="relative group cursor-pointer">
                    <div
                      onClick={() => openGallery(project, 0)}
                      className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-lg"
                    >
                      <img
                        src={project.images[0]}
                        alt={project.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full px-6 py-3 text-sm font-semibold text-charcoal">
                          View Gallery ({project.images.length} photos)
                        </div>
                      </div>
                    </div>

                    {/* Thumbnail Grid */}
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      {project.images.slice(1, 4).map((img, idx) => (
                        <div
                          key={idx}
                          onClick={() => openGallery(project, idx + 1)}
                          className="relative aspect-[4/3] rounded-lg overflow-hidden cursor-pointer group/thumb"
                        >
                          <img
                            src={img}
                            alt={`${project.name} ${idx + 2}`}
                            className="w-full h-full object-cover group-hover/thumb:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-black/20 group-hover/thumb:bg-black/40 transition-colors" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="w-full lg:w-1/2 space-y-6">
                  <div>
                    {project.codename && (
                      <div className="text-sm font-mono text-gold mb-2 tracking-wider">
                        {project.codename}
                      </div>
                    )}
                    <h3 className="text-3xl md:text-4xl font-bold text-charcoal mb-3">
                      {project.name}
                    </h3>
                    <p className="text-xl text-charcoal-400 font-medium mb-4">
                      {project.tagline}
                    </p>
                    <p className="text-charcoal-400 leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  {/* Project Info */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-charcoal-400">
                      <MapPin size={18} className="text-gold flex-shrink-0" />
                      <span className="text-sm font-medium">{project.location}</span>
                    </div>
                    {project.scale && (
                      <div className="flex items-center space-x-2 text-charcoal-400">
                        <Home size={18} className="text-gold flex-shrink-0" />
                        <span className="text-sm font-medium">{project.scale}</span>
                      </div>
                    )}
                    {!project.scale && (
                      <div className="flex items-center space-x-2 text-charcoal-400">
                        <Home size={18} className="text-gold flex-shrink-0" />
                        <span className="text-sm font-medium">{project.unitTypes.join(' • ')}</span>
                      </div>
                    )}
                    <Badge 
                      variant={project.status.includes('Ready') ? 'default' : 'secondary'}
                      className="px-3 py-1"
                    >
                      <Clock size={14} className="mr-1" />
                      {project.status}
                    </Badge>
                  </div>

                  {/* Key Highlights or Amenities */}
                  <div>
                    <h4 className="text-sm font-semibold text-charcoal uppercase tracking-wider mb-3">
                      {project.keyHighlights ? 'Key Highlights' : 'Key Amenities'}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {project.keyHighlights ? (
                        project.keyHighlights.slice(0, 6).map((highlight, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                            <span className="text-sm text-charcoal-400">{highlight}</span>
                          </div>
                        ))
                      ) : (
                        project.amenities.slice(0, 6).map((amenity, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                            <span className="text-sm text-charcoal-400">{amenity}</span>
                          </div>
                        ))
                      )}
                    </div>
                    <button
                      onClick={() => openGallery(project, 0)}
                      className="text-sm text-gold hover:text-gold-600 font-medium mt-2 inline-flex items-center"
                    >
                      View all details
                      <ChevronRight size={16} className="ml-1" />
                    </button>
                  </div>

                  {/* Connectivity (if available) */}
                  {project.connectivity && (
                    <div>
                      <h4 className="text-sm font-semibold text-charcoal uppercase tracking-wider mb-3">
                        Connectivity
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {project.connectivity.map((conn, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg px-3 py-2">
                            <div className="text-xs text-gray-500">{conn.location}</div>
                            <div className="text-sm font-semibold text-charcoal">{conn.time}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="flex flex-wrap gap-3 pt-4">
                    <Button
                      onClick={scrollToContact}
                      className="bg-gold hover:bg-gold-600 text-white px-6"
                    >
                      Schedule Visit
                    </Button>
                    <Button
                      onClick={() => openGallery(project, 0)}
                      variant="outline"
                      className="border-gray-300 hover:bg-gray-50"
                    >
                      View Gallery
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      <Dialog open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
        <DialogContent className="max-w-6xl w-full p-0 overflow-hidden">
          {selectedProject && (
            <div className="relative">
              {/* Close Button */}
              <button
                onClick={closeGallery}
                className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              >
                <X size={20} />
              </button>

              {/* Main Image */}
              <div className="relative aspect-[16/10] bg-gray-900">
                <img
                  src={selectedProject.images[selectedImageIndex]}
                  alt={`${selectedProject.name} ${selectedImageIndex + 1}`}
                  className="w-full h-full object-contain"
                />

                {/* Navigation Arrows */}
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors"
                >
                  <ChevronRight size={24} className="rotate-180" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors"
                >
                  <ChevronRight size={24} />
                </button>

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                  {selectedImageIndex + 1} / {selectedProject.images.length}
                </div>
              </div>

              {/* Thumbnails */}
              <div className="flex gap-2 p-4 overflow-x-auto bg-gray-50">
                {selectedProject.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === selectedImageIndex
                        ? 'border-gold scale-105'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Project Info in Modal */}
              <div className="p-6 bg-white border-t">
                <h3 className="text-2xl font-bold text-charcoal mb-2">
                  {selectedProject.name}
                </h3>
                <p className="text-charcoal-400 mb-4">{selectedProject.tagline}</p>
                
                {/* Project Meta Info */}
                <div className="mb-6 space-y-2">
                  <div className="flex items-center text-charcoal-400">
                    <MapPin size={16} className="text-gold mr-2" />
                    <span className="text-sm font-medium">{selectedProject.location}</span>
                  </div>
                  {selectedProject.scale && (
                    <div className="flex items-center text-charcoal-400">
                      <Home size={16} className="text-gold mr-2" />
                      <span className="text-sm font-medium">{selectedProject.scale}</span>
                    </div>
                  )}
                  {selectedProject.launchDate && (
                    <div className="flex items-center text-charcoal-400">
                      <Clock size={16} className="text-gold mr-2" />
                      <span className="text-sm font-medium">Launch: {selectedProject.launchDate}</span>
                    </div>
                  )}
                </div>

                {/* Key Highlights */}
                {selectedProject.keyHighlights && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-charcoal uppercase tracking-wider mb-3">
                      Key Highlights
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {selectedProject.keyHighlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                          <span className="text-sm text-charcoal-400">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Amenities */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-charcoal uppercase tracking-wider mb-3">
                    {selectedProject.keyHighlights ? 'Amenities & Facilities' : 'All Amenities'}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {selectedProject.amenities.map((amenity, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                        <span className="text-sm text-charcoal-400">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Connectivity */}
                {selectedProject.connectivity && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-charcoal uppercase tracking-wider mb-3">
                      Connectivity
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {selectedProject.connectivity.map((conn, idx) => (
                        <div key={idx} className="bg-gold-50 rounded-lg px-3 py-3 text-center border border-gold-100">
                          <div className="text-xs text-charcoal-400 mb-1">{conn.location}</div>
                          <div className="text-sm font-bold text-gold">{conn.time}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => {
                    closeGallery();
                    scrollToContact();
                  }}
                  className="w-full bg-gold hover:bg-gold-600 text-white"
                >
                  Schedule a Visit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
